import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Home, BedDouble, Bath, Sofa, MapPin, Mail, Phone, FileText, ChevronLeft, ChevronRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loading, EmptyState, StatusBadge } from "@/components/common";
import { api, useFetch, errMsg, fmtDate } from "@/lib/api";
import { priceText, STATUS_LABEL } from "@/pages/biz/Listings";
import { BookViewing, useRecordView } from "@/components/BookViewing";

function TopBar() {
  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2"><div className="w-8 h-8 rounded-lg bg-emerald-700 flex items-center justify-center"><Home className="w-4 h-4 text-white" /></div><span className="font-display text-xl font-extrabold">PropNest</span></Link>
        <Link to="/properties" className="text-sm font-semibold text-slate-700 hover:text-slate-900" data-testid="topbar-search-link">Find a property</Link>
      </div>
    </header>
  );
}

function Card({ x }) {
  return (
    <Link to={`/properties/${x.id}`} className="group bg-white border border-slate-200 rounded-xl overflow-hidden lift" data-testid={`public-listing-${x.id}`}>
      <div className="aspect-[4/3] bg-slate-100 overflow-hidden relative">
        {x.photos[0] && <img src={x.photos[0].url} alt={x.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />}
        {x.status !== "available" && <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-slate-900 text-white text-xs font-semibold">{STATUS_LABEL[x.status]}</span>}
      </div>
      <div className="p-5">
        <div className="font-display text-xl font-extrabold">{priceText(x, "GBP")}</div>
        <h3 className="font-semibold mt-1">{x.title}</h3>
        <div className="text-sm text-slate-500 flex items-center gap-1 mt-1"><MapPin className="w-3.5 h-3.5" />{x.display_address}</div>
        <div className="flex gap-4 text-sm text-slate-600 mt-3"><span className="flex items-center gap-1"><BedDouble className="w-4 h-4" />{x.bedrooms}</span><span className="flex items-center gap-1"><Bath className="w-4 h-4" />{x.bathrooms}</span><span className="flex items-center gap-1"><Sofa className="w-4 h-4" />{x.receptions}</span></div>
      </div>
    </Link>
  );
}

export function PropertySearch() {
  const [f, setF] = useState({ channel: "letting", q: "", beds: "0", max_price: "" });
  const qs = new URLSearchParams({ channel: f.channel, ...(f.q && { q: f.q }), ...(f.beds !== "0" && { beds: f.beds }), ...(f.max_price && { max_price: f.max_price }) }).toString();
  const { data } = useFetch(`/public/listings?${qs}`);
  return (
    <div className="min-h-screen bg-slate-50" data-testid="property-search-page">
      <TopBar />
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="eyebrow mb-3">Property search</div>
        <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight mb-8">Find your next home</h1>
        <div className="panel grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-10">
          <Select value={f.channel} onValueChange={(v) => setF({ ...f, channel: v })}><SelectTrigger data-testid="search-channel"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="letting">To rent</SelectItem><SelectItem value="sale">For sale</SelectItem></SelectContent></Select>
          <Input placeholder="Area, street or keyword" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} data-testid="search-q" />
          <Select value={f.beds} onValueChange={(v) => setF({ ...f, beds: v })}><SelectTrigger data-testid="search-beds"><SelectValue /></SelectTrigger>
            <SelectContent>{["0", "1", "2", "3", "4", "5"].map((b) => <SelectItem key={b} value={b}>{b === "0" ? "Any bedrooms" : `${b}+ bedrooms`}</SelectItem>)}</SelectContent></Select>
          <Input type="number" placeholder={f.channel === "letting" ? "Max rent" : "Max price"} value={f.max_price} onChange={(e) => setF({ ...f, max_price: e.target.value })} data-testid="search-max-price" />
        </div>
        {!data ? <Loading /> : data.length === 0 ? <EmptyState icon={Home} title="No properties match" text="Try widening your search." /> : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">{data.map((x) => <Card key={x.id} x={x} />)}</div>
        )}
      </div>
    </div>
  );
}

function Enquiry({ id }) {
  const [f, setF] = useState({ name: "", email: "", phone: "", message: "I'd like to arrange a viewing." });
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post(`/public/listings/${id}/enquiry`, f); setSent(true); } catch (err) { toast.error(errMsg(err)); }
  };
  if (sent) return <div className="panel text-sm" data-testid="enquiry-sent">Thanks — your enquiry has been sent. The agent will be in touch soon.</div>;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <form onSubmit={submit} className="panel space-y-3" data-testid="enquiry-form">
      <h3 className="text-lg font-bold">Contact the agent</h3>
      <Input required placeholder="Your name" value={f.name} onChange={set("name")} data-testid="enquiry-name" />
      <Input required type="email" placeholder="Email" value={f.email} onChange={set("email")} data-testid="enquiry-email" />
      <Input placeholder="Phone" value={f.phone} onChange={set("phone")} data-testid="enquiry-phone" />
      <Textarea rows={4} value={f.message} onChange={set("message")} data-testid="enquiry-message" />
      <Button type="submit" className="w-full bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="enquiry-submit">Send enquiry</Button>
    </form>
  );
}

const Row = ({ k, v }) => (v || v === 0 ? <div className="flex justify-between py-2 border-b border-slate-100 text-sm"><span className="text-slate-500">{k}</span><span className="font-medium capitalize">{String(v).replace(/_/g, " ")}</span></div> : null);

export function PropertyPage() {
  const { id } = useParams();
  const { data: x, loading } = useFetch(`/public/listings/${id}`, true);
  const [i, setI] = useState(0);
  useRecordView(id);
  if (loading) return <Loading full />;
  if (!x) return <div className="min-h-screen bg-slate-50"><TopBar /><div className="max-w-3xl mx-auto p-12"><EmptyState icon={Home} title="This property is no longer available" /></div></div>;
  const letting = x.channel === "letting";
  const photo = x.photos[i];
  return (
    <div className="min-h-screen bg-slate-50" data-testid="property-page">
      <TopBar />
      <div className="max-w-7xl mx-auto px-6 py-10 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div className="relative rounded-2xl overflow-hidden bg-slate-200 aspect-[16/10]">
            {photo && <img src={photo.url} alt={photo.caption || x.title} className="w-full h-full object-cover" data-testid="gallery-image" />}
            {x.photos.length > 1 && <>
              <button onClick={() => setI((i - 1 + x.photos.length) % x.photos.length)} className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 rounded-full p-2" data-testid="gallery-prev"><ChevronLeft className="w-5 h-5" /></button>
              <button onClick={() => setI((i + 1) % x.photos.length)} className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 rounded-full p-2" data-testid="gallery-next"><ChevronRight className="w-5 h-5" /></button>
              <span className="absolute bottom-3 right-3 bg-slate-900/80 text-white text-xs px-2 py-1 rounded">{i + 1} / {x.photos.length}</span>
            </>}
          </div>
          <div>
            <div className="flex items-center gap-2"><span className="eyebrow">{letting ? "To rent" : "For sale"}</span>{x.status !== "available" && <StatusBadge status="due" label={STATUS_LABEL[x.status]} />}</div>
            <h1 className="text-3xl sm:text-4xl font-extrabold mt-2" data-testid="property-title">{x.title}</h1>
            <p className="text-slate-500 mt-1 flex items-center gap-1"><MapPin className="w-4 h-4" />{x.display_address} {x.postcode_district}</p>
            <div className="font-display text-3xl font-extrabold text-emerald-700 mt-4" data-testid="property-price">{priceText(x, "GBP")}</div>
          </div>
          {x.features?.length > 0 && <div className="panel"><h3 className="text-lg font-bold mb-3">Key features</h3><ul className="grid sm:grid-cols-2 gap-2 text-sm list-disc pl-5">{x.features.map((f) => <li key={f}>{f}</li>)}</ul></div>}
          <div className="panel"><h3 className="text-lg font-bold mb-3">Description</h3><p className="text-sm leading-relaxed whitespace-pre-wrap text-slate-700">{x.description}</p></div>
          {x.documents?.length > 0 && <div className="panel"><h3 className="text-lg font-bold mb-3">Floorplans & documents</h3><div className="flex flex-wrap gap-3">{x.documents.map((d) => (
            <a key={d.url} href={d.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 text-sm hover:bg-slate-50 capitalize"><FileText className="w-4 h-4 text-emerald-700" />{d.caption || d.kind}</a>))}</div></div>}
        </div>
        <div className="space-y-6">
          <div className="panel">
            <Row k="Property type" v={x.property_type} /><Row k="Bedrooms" v={x.bedrooms} /><Row k="Bathrooms" v={x.bathrooms} /><Row k="Receptions" v={x.receptions} />
            <Row k="Size" v={x.size_sqft ? `${x.size_sqft} sq ft` : null} />
            {letting ? <><Row k="Available from" v={x.available_date ? fmtDate(x.available_date) : "Now"} /><Row k="Deposit" v={x.deposit ? `£${x.deposit}` : null} /><Row k="Furnishing" v={x.furnishing} /><Row k="Let type" v={x.let_type} /></>
              : <><Row k="Tenure" v={x.tenure} /><Row k="Lease remaining" v={x.lease_years ? `${x.lease_years} years` : null} /><Row k="Service charge" v={x.service_charge ? `£${x.service_charge}/yr` : null} /><Row k="Ground rent" v={x.ground_rent ? `£${x.ground_rent}/yr` : null} /><Row k="Chain" v={x.chain_free ? "Chain free" : null} /></>}
            <Row k="Council tax band" v={x.council_tax_band} /><Row k="EPC rating" v={x.epc_rating} /><Row k="Parking" v={x.parking} /><Row k="Garden" v={x.garden} />
          </div>
          <div className="panel" data-testid="property-agent">
            <div className="text-xs uppercase tracking-wider text-slate-500">Marketed by</div>
            <Link to={`/agents/${x.agent?.id}`} className="font-bold text-lg hover:underline" data-testid="property-agent-link">{x.agent?.name}</Link>
            <div className="text-sm text-slate-600 mt-2 space-y-1">
              {x.agent?.phone && <a href={`tel:${x.agent.phone}`} className="flex items-center gap-2"><Phone className="w-4 h-4" />{x.agent.phone}</a>}
              {x.agent?.email && <a href={`mailto:${x.agent.email}`} className="flex items-center gap-2"><Mail className="w-4 h-4" />{x.agent.email}</a>}
            </div>
          </div>
          <BookViewing id={id} />
          <Enquiry id={id} />
        </div>
      </div>
    </div>
  );
}

export function AgentPage() {
  const { id } = useParams();
  const { data: a, loading } = useFetch(`/public/agents/${id}`, true);
  if (loading) return <Loading full />;
  if (!a) return <div className="min-h-screen bg-slate-50"><TopBar /><div className="max-w-3xl mx-auto p-12"><EmptyState icon={Home} title="Profile not found" /></div></div>;
  return (
    <div className="min-h-screen bg-slate-50" data-testid="agent-page">
      <TopBar />
      <div className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 py-14 grid md:grid-cols-3 gap-8">
          <div className="md:col-span-2">
            <div className="text-xs uppercase tracking-[0.2em] text-emerald-400 mb-3">{a.type === "agent" ? "Letting & estate agent" : "Landlord"}</div>
            <h1 className="text-4xl sm:text-5xl font-extrabold" data-testid="agent-name">{a.name}</h1>
            {a.about && <p className="text-slate-300 mt-4 max-w-2xl whitespace-pre-wrap" data-testid="agent-about">{a.about}</p>}
          </div>
          <div className="space-y-2 text-sm text-slate-200" data-testid="agent-contact">
            {a.address && <div className="flex gap-2"><MapPin className="w-4 h-4 mt-0.5" />{a.address}</div>}
            {a.phone && <a href={`tel:${a.phone}`} className="flex gap-2"><Phone className="w-4 h-4" />{a.phone}</a>}
            {a.email && <a href={`mailto:${a.email}`} className="flex gap-2"><Mail className="w-4 h-4" />{a.email}</a>}
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-6 py-12">
        <h2 className="text-2xl font-extrabold mb-6">Available properties ({a.listings.length})</h2>
        {a.listings.length === 0 ? <EmptyState icon={Home} title="No properties on the market right now" /> : <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">{a.listings.map((x) => <Card key={x.id} x={x} />)}</div>}
      </div>
    </div>
  );
}
